// Warm-lead invite landing — recipient clicks the URL the operator
// pasted into LinkedIn / WhatsApp / email. Fetches invite metadata
// (company hint, expiry), shows a single email field, consumes the
// invite and redirects to /login/verify with the issued magic-link
// token so the recipient lands directly in /app/welcome.
//
// Visual: Orbis Brand Kit v1.0 (Geist sans, ink/bg/signal palette,
// system bar with pulse, no serif, no em-dashes).

import { useCallback, useEffect, useState } from 'react';
import { apiBase } from '../../lib/api';
import { useParams } from 'react-router-dom';
import { Turnstile } from '../../components/Turnstile';

const API_BASE = apiBase();

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; company: string; emailHint: string | null }
  | { kind: 'expired' }
  | { kind: 'not_found' }
  | { kind: 'already_consumed' };

type SubmitState = 'idle' | 'sending' | 'captcha_failed' | 'error';

export function Invite() {
  const { token } = useParams<{ token: string }>();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submit, setSubmit] = useState<SubmitState>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('orbis-kit');
    return () => document.body.classList.remove('orbis-kit');
  }, []);

  useEffect(() => {
    if (!token) {
      setLoad({ kind: 'not_found' });
      return;
    }
    fetch(`${API_BASE}/api/public/invites/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 404) return setLoad({ kind: 'not_found' });
        if (r.status === 410) {
          const body = (await r.json().catch(() => ({}))) as { error?: string };
          return setLoad({
            kind: body.error === 'already_consumed' ? 'already_consumed' : 'expired',
          });
        }
        if (!r.ok) return setLoad({ kind: 'not_found' });
        const body = (await r.json()) as { company_name: string; email_hint: string | null };
        setLoad({ kind: 'ready', company: body.company_name, emailHint: body.email_hint });
        if (body.email_hint && !email) setEmail(body.email_hint);
      })
      .catch(() => setLoad({ kind: 'not_found' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onVerify = useCallback((t: string) => setTurnstileToken(t), []);
  const onExpire = useCallback(() => setTurnstileToken(null), []);

  async function consume(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSubmit('sending');
    setErrMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/invites/${encodeURIComponent(token)}/consume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_email: email, turnstileToken }),
      });
      if (r.status === 403) {
        setSubmit('captcha_failed');
        return;
      }
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        setErrMsg(prettyError(body.error));
        setSubmit('error');
        return;
      }
      const body = (await r.json()) as { verify_url: string };
      // Hard navigate so the browser hits the worker callback that mints
      // the brand cookie. SPA navigate would skip the worker entirely.
      window.location.href = body.verify_url;
    } catch {
      setSubmit('error');
      setErrMsg('Something went wrong. Try again.');
    }
  }

  return (
    <Shell>
      {load.kind === 'loading' && (
        <Center>
          <Muted>Loading.</Muted>
        </Center>
      )}

      {load.kind === 'not_found' && (
        <Card>
          <H1>Invite not found.</H1>
          <Lead>
            This link is broken or has been recalled. Reach out at{' '}
            <Mail>hello@imorbis.com</Mail> for a fresh one.
          </Lead>
        </Card>
      )}

      {load.kind === 'expired' && (
        <Card>
          <H1>This invite has expired.</H1>
          <Lead>
            Email <Mail>hello@imorbis.com</Mail> and we'll send a new one.
          </Lead>
        </Card>
      )}

      {load.kind === 'already_consumed' && (
        <Card>
          <H1>This invite was already used.</H1>
          <Lead>
            If you signed up earlier, head to{' '}
            <a href="/login" style={{ color: 'var(--okit-ink)', fontWeight: 600 }}>
              sign in
            </a>
            . If this looks wrong, email <Mail>hello@imorbis.com</Mail>.
          </Lead>
        </Card>
      )}

      {load.kind === 'ready' && (
        <Card>
          <Eyebrow>Welcome</Eyebrow>
          <H1>{load.company}, you're set up.</H1>
          <Lead>
            Confirm your email and we'll send you straight in. No password.
          </Lead>

          <form
            onSubmit={consume}
            style={{
              marginTop: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <Field
              label="Work email"
              value={email}
              onChange={setEmail}
              placeholder="you@company.com"
              type="email"
            />
            <Turnstile onVerify={onVerify} onExpire={onExpire} theme="light" />
            <PrimaryButton onClick={() => undefined} disabled={!email || submit === 'sending'} busy={submit === 'sending'}>
              Take me in
            </PrimaryButton>
            {submit === 'captcha_failed' && (
              <ErrorLine text="Please complete the verification and try again." />
            )}
            {submit === 'error' && <ErrorLine text={errMsg ?? 'Something went wrong.'} />}
          </form>
        </Card>
      )}
    </Shell>
  );
}

function prettyError(code: string | undefined): string {
  switch (code) {
    case 'invalid_email':
      return "That email doesn't look right.";
    case 'expired':
      return 'This invite has expired. Ask for a new one.';
    case 'already_consumed':
      return 'This invite was already used.';
    case 'not_found':
      return 'Invite not found.';
    default:
      return 'Something went wrong. Try again.';
  }
}

// ── Building blocks (Brand Kit) ────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SystemBar />
      <main
        style={{
          minHeight: '100vh',
          padding: '64px 24px 96px',
          background: 'var(--okit-bg)',
        }}
      >
        <div style={{ maxWidth: 520, margin: '0 auto' }}>{children}</div>
      </main>
    </>
  );
}

function SystemBar() {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: '12px 24px',
        background: 'var(--okit-ink)',
        color: 'var(--okit-bg)',
        fontFamily: "'Geist Mono', monospace",
        fontSize: 12,
        letterSpacing: '-0.005em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span style={{ fontWeight: 600 }}>orbis · studio</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#9CA0A6' }}>
        <Pulse /> brand · invite
      </span>
    </header>
  );
}

function Pulse() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: 'var(--okit-signal)',
        animation: 'okitPulse 1.6s ease-out infinite',
      }}
    />
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--okit-surface)',
        border: '1px solid var(--okit-line)',
        borderRadius: 14,
        padding: 40,
      }}
    >
      {children}
    </section>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "'Geist Mono', monospace",
        fontSize: 11,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--okit-ink-mute)',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

function H1({ children }: { children: React.ReactNode }) {
  return (
    <h1
      style={{
        fontFamily: 'inherit',
        fontSize: 'clamp(28px, 3.6vw, 36px)',
        fontWeight: 600,
        letterSpacing: '-0.025em',
        color: 'var(--okit-ink)',
        lineHeight: 1.1,
        margin: 0,
      }}
    >
      {children}
    </h1>
  );
}

function Lead({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        marginTop: 14,
        fontSize: 16,
        lineHeight: 1.55,
        color: 'var(--okit-ink-soft)',
        maxWidth: '52ch',
      }}
    >
      {children}
    </p>
  );
}

function Mail({ children }: { children: React.ReactNode }) {
  return (
    <a href={`mailto:${children}`} style={{ color: 'var(--okit-ink)', fontWeight: 600 }}>
      {children}
    </a>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p style={{ color: 'var(--okit-ink-mute)' }}>{children}</p>;
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>{children}</div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--okit-ink-soft)',
          marginBottom: 8,
        }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        autoFocus
        style={{
          width: '100%',
          padding: '12px 14px',
          background: 'var(--okit-bg)',
          color: 'var(--okit-ink)',
          border: '1px solid var(--okit-line-strong)',
          borderRadius: 10,
          fontSize: 15,
          fontFamily: 'inherit',
          outline: 'none',
        }}
      />
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  busy,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="submit"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '13px 22px',
        background: 'var(--okit-ink)',
        color: 'var(--okit-bg)',
        border: 'none',
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {busy ? 'Working.' : children}
    </button>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <p
      style={{
        marginTop: 4,
        padding: '10px 12px',
        background: 'rgba(212, 58, 47, 0.06)',
        border: '1px solid rgba(212, 58, 47, 0.25)',
        borderRadius: 8,
        color: 'var(--okit-warn)',
        fontSize: 13,
      }}
    >
      {text}
    </p>
  );
}
